import { Download } from "lucide-react";
import { Waveform } from "./Waveform";
import { HeatStrip } from "./HeatStrip";
import { STEM_META, absUrl } from "@/lib/api";
import { fmtRate, fmtBytes } from "@/lib/format";

const HEAT = {
  me: { key: "me_dialogue_leakage", label: "suspected dialogue leakage", color: "#F43F5E" },
  dialogue: { key: "dx_nonspeech_energy", label: "non-speech energy in DX (music/FX bleed)", color: "#F59E0B" },
};

export const StemLane = ({ stem, file, player, missing, analysis }) => {
  const meta = STEM_META[stem];
  const heat = HEAT[stem] && analysis?.[HEAT[stem].key];
  const on = player.audible(stem);
  const soloed = player.solo.includes(stem), mutedFlag = player.muted.includes(stem);
  const abLocked = player.ab !== "free";
  return (
    <div data-testid={`qc-stem-lane-${stem}`} className={`grid grid-cols-[210px_1fr] gap-3 items-stretch rounded-md border transition-colors ${soloed ? "border-[#3A4A68] bg-[#151b27]" : "border-[#1c2433] bg-[#121721]"}`}>
      <div className="p-3 flex flex-col justify-between border-r border-[#1c2433]">
        <div>
          <div className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-sm" style={{ background: meta.color }} />
            <span className="font-mono text-[11px] font-bold tracking-widest" style={{ color: meta.color }}>{meta.short}</span>
          </div>
          <div className="text-sm font-medium text-slate-100 mt-1 leading-tight">{meta.label}</div>
          {file ? (
            <div className="text-[10px] font-mono text-slate-500 mt-1">{fmtRate(file.sample_rate)} · {file.bit_depth ? `${file.bit_depth}-bit` : ""} · {file.channels}ch · {fmtBytes(file.size)}</div>
          ) : (
            <div className="text-[10px] font-mono text-amber-500 mt-1">{missing || "not requested"}</div>
          )}
        </div>
        <div className="flex items-center gap-1.5 mt-2">
          <button data-testid={`qc-solo-${stem}-button`} disabled={!file || abLocked} onClick={() => player.toggleSolo(stem)}
            className={`h-7 w-8 rounded-sm text-xs font-bold border transition-colors disabled:opacity-30 ${soloed ? "bg-amber-400 text-black border-amber-400" : "border-[#3A4A68] text-slate-300 hover:bg-[#1E2738]"}`}>S</button>
          <button data-testid={`qc-mute-${stem}-button`} disabled={!file || abLocked} onClick={() => player.toggleMute(stem)}
            className={`h-7 w-8 rounded-sm text-xs font-bold border transition-colors disabled:opacity-30 ${mutedFlag ? "bg-rose-500 text-white border-rose-500" : "border-[#3A4A68] text-slate-300 hover:bg-[#1E2738]"}`}>M</button>
          <span className={`ml-1 h-2 w-2 rounded-full ${on && file ? "bg-emerald-400" : "bg-slate-700"}`} title={on ? "audible" : "silent"} />
          {file && (
            <a data-testid={`qc-download-${stem}-button`} href={absUrl(file.download_url)} className="ml-auto flex items-center gap-1 px-2 h-7 rounded-sm border border-[#3A4A68] text-[11px] text-slate-300 hover:bg-[#1E2738]" title="Download WAV">
              <Download size={12} /> WAV
            </a>
          )}
        </div>
      </div>
      <div className="p-2 pr-3 flex flex-col justify-center">
        <Waveform peaks={file?.peaks || []} color={meta.color} progress={player.duration ? player.time / player.duration : 0}
          dim={!on} onSeek={(p) => player.seek(p * player.duration)} testId={`qc-waveform-${stem}`} />
        {heat && (
          <div className="flex items-center gap-2">
            <div className="flex-1"><HeatStrip values={heat} color={HEAT[stem].color} progress={player.duration ? player.time / player.duration : 0} onSeek={(p) => player.seek(p * player.duration)} testId={`qc-heatmap-${stem}`} /></div>
            <span className="text-[10px] font-mono whitespace-nowrap mt-1" style={{ color: HEAT[stem].color }}>{HEAT[stem].label}</span>
          </div>
        )}
      </div>
    </div>
  );
};
